import type React from "react";

export type HeroSectionProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  description: string;
  primaryCta: { label: string; href: string };
  secondaryCta?: { label: string; href: string; isExternal?: boolean };
};

const HeroSection: React.FC<HeroSectionProps> = ({
  eyebrow,
  title,
  subtitle,
  description,
  primaryCta,
  secondaryCta,
}) => {
  return (
    <section className="relative overflow-hidden bg-primary-900 text-white">
      <div className="absolute -right-24 top-1/2 hidden h-[500px] w-[500px] -translate-y-1/2 rounded-full border border-white/10 bg-primary-700/20 blur-3xl lg:block" />
      <div className="container grid min-h-[70vh] items-center gap-16 py-24 lg:grid-cols-[1.05fr_1fr]">
        <div className="flex flex-col gap-6 text-left">
          <span className="inline-flex w-fit items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-primary-100">
            {eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {title}
          </h1>
          <p className="max-w-2xl text-lg text-primary-100 sm:text-xl">
            {subtitle}
          </p>
          <p className="max-w-2xl text-base text-primary-100/80 sm:text-lg">
            {description}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a href={primaryCta.href} className="btn-primary">
              {primaryCta.label}
            </a>
            {secondaryCta ? (
              <a
                href={secondaryCta.href}
                target={secondaryCta.isExternal ? "_blank" : undefined}
                rel={secondaryCta.isExternal ? "noreferrer" : undefined}
                className="btn-outline border-white text-white hover:border-white/80 hover:bg-white/10 hover:text-white"
              >
                {secondaryCta.isExternal ? (
                  <span>
                    {secondaryCta.label}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </span>
                ) : (
                  secondaryCta.label
                )}
              </a>
            ) : null}
          </div>
        </div>

        <div className="relative isolate overflow-hidden rounded-3xl border border-white/15 bg-white/5 p-8 text-left shadow-2xl backdrop-blur-sm sm:p-12">
          <div className="absolute -top-20 right-6 hidden h-40 w-40 rounded-full border border-white/20 bg-primary-500/20 opacity-70 blur-3xl sm:block" />
          <div className="absolute -bottom-24 left-1/2 hidden h-48 w-48 -translate-x-1/2 rounded-full border border-white/20 bg-primary-400/10 opacity-60 blur-3xl md:block" />
          <div className="relative flex flex-col gap-6">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-100/80">
                Foundation priorities
              </div>
              <p className="mt-2 text-2xl font-semibold text-white">
                Scaling support for families, providers, and neighborhood
                partners across Washington, D.C.
              </p>
            </div>
            <dl className="grid gap-5 text-sm text-primary-50/90">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.26em] text-primary-100/70">
                  Current focus
                </dt>
                <dd className="mt-2 text-sm font-medium text-white">
                  Building grant-ready infrastructure to fuel sustainable
                  service delivery.
                </dd>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.26em] text-primary-100/70">
                  Next milestone
                </dt>
                <dd className="mt-2 text-sm font-medium text-white">
                  Launch coordinated pantry and resource network with community
                  partners.
                </dd>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.26em] text-primary-100/70">
                  How to help
                </dt>
                <dd className="mt-2 text-sm font-medium text-white">
                  Collaborate on funding, volunteer mobilization, or strategic
                  planning support.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
